import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {catchError, exhaustMap, filter, finalize, map, mergeMap, of, switchMap, tap} from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';
import { AnagraficheActions } from './anagrafiche.actions';
import {
  selectClientiListLastQuery,
  selectClientiLoaded,
  selectFornitoriListLastQuery,
  selectFornitoriLoaded, selectSelectedCliente, selectSelectedFornitore,
} from './anagrafiche.selectors';
import { FornitoriPort } from '../../core/services/fornitori/fornitori.port';
import { ClientiPort } from '../../core/services/clienti/clienti.port';
import { LoadingService } from '../../core/services/loading/loading.service';
import { SwalService } from '../../core/services/alerts/swal.service';
import { CantieriActions } from '../cantieri/cantieri.actions';

@Injectable()
export class AnagraficheEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private fornitoriApi = inject(FornitoriPort);
  private clientiApi = inject(ClientiPort);
  private loading = inject(LoadingService);
  private swalService = inject(SwalService);

  // ========================================
  // ATTORE (create unificato)
  // ========================================
  createAttore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.createAttore),
      exhaustMap(({ dto }) => {
        this.loading.show();
        return this.fornitoriApi.create(dto).pipe(
          map(item => AnagraficheActions.createAttoreSuccess({ item })),
          catchError(err => of(AnagraficheActions.createAttoreFailure({ error: err?.message ?? 'Errore creazione anagrafica' }))),
          finalize(() => this.loading.hide()),
        );
      })
    )
  );

  // ========================================
  // FORNITORI
  // ========================================
  loadFornitori$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.loadFornitori),
      withLatestFrom(this.store.select(selectFornitoriLoaded)),
      filter(([, loaded]) => !loaded),
      exhaustMap(() => {
        return this.fornitoriApi.getList().pipe(
          map(items => AnagraficheActions.loadFornitoriSuccess({ items })),
          catchError(err => of(AnagraficheActions.loadFornitoriFailure({ error: err?.message ?? 'Errore caricamento fornitori' }))),
        );
      })
    )
  );

  loadFornitoreDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.loadFornitoreDetails),
      exhaustMap(({ id }) => {
        this.loading.show();
        return this.fornitoriApi.getById(id).pipe(
          map(item => AnagraficheActions.loadFornitoreDetailsSuccess({ item })),
          catchError(err => of(AnagraficheActions.loadFornitoreDetailsFailure({ error: err?.message ?? 'Errore dettagli fornitore' }))),
          finalize(() => this.loading.hide()),
        );
      })
    )
  );

  createFornitore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.createFornitore),
      exhaustMap(({ dto }) => {
        this.loading.show();
        return this.fornitoriApi.create(dto).pipe(
          map(item => AnagraficheActions.createFornitoreSuccess({ item })),
          catchError(err => of(AnagraficheActions.createFornitoreFailure({ error: err?.message ?? 'Errore creazione fornitore' }))),
          finalize(() => this.loading.hide()),
        );
      })
    )
  );

  updateFornitore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.updateFornitore),
      exhaustMap(({ id, changes }) => {
        this.loading.show();
        return this.fornitoriApi.update(id, changes).pipe(
          map(item => AnagraficheActions.updateFornitoreSuccess({ item })),
          catchError(err => of(AnagraficheActions.updateFornitoreFailure({ error: err?.message ?? 'Errore modifica fornitore' }))),
          finalize(() => this.loading.hide()),
        );
      })
    )
  );

  deleteFornitore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.deleteFornitore),
      exhaustMap(({ id }) => {
        this.loading.show();
        return this.fornitoriApi.delete(id).pipe(
          map(() => AnagraficheActions.deleteFornitoreSuccess({ id })),
          catchError(err => of(AnagraficheActions.deleteFornitoreFailure({ error: err?.message ?? 'Errore eliminazione fornitore' }))),
          finalize(() => this.loading.hide()),
        );
      })
    )
  );

  loadFornitoriPaginated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.loadFornitoriPaginated),
      exhaustMap(({ params }) =>
        this.fornitoriApi.getListPaginated(params ?? {}).pipe(
          map(res => AnagraficheActions.loadFornitoriPaginatedSuccess({
            items: res.content,
            totalElements: res.totalElements,
            totalPages: res.totalPages,
            pageIndex: res.pageIndex,
          })),
          catchError(err => of(AnagraficheActions.loadFornitoriPaginatedFailure({ error: err?.message ?? 'Errore caricamento fornitori' }))),
        )
      )
    )
  );

  // ========================================
  // CLIENTI
  // ========================================
  loadClienti$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.loadClienti),
      withLatestFrom(this.store.select(selectClientiLoaded)),
      filter(([, loaded]) => !loaded),
      exhaustMap(() => {
        return this.clientiApi.getList().pipe(
          map(items => AnagraficheActions.loadClientiSuccess({ items })),
          catchError(err => of(AnagraficheActions.loadClientiFailure({ error: err?.message ?? 'Errore caricamento clienti' }))),
        );
      })
    )
  );

  loadClienteDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.loadClienteDetails),
      exhaustMap(({ id }) => {
        this.loading.show();
        return this.clientiApi.getById(id).pipe(
          map(item => AnagraficheActions.loadClienteDetailsSuccess({ item })),
          catchError(err => of(AnagraficheActions.loadClienteDetailsFailure({ error: err?.message ?? 'Errore dettagli cliente' }))),
          finalize(() => this.loading.hide()),
        );
      })
    )
  );

  createCliente$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.createCliente),
      exhaustMap(({ dto }) => {
        this.loading.show();
        return this.clientiApi.create(dto).pipe(
          map(item => AnagraficheActions.createClienteSuccess({ item })),
          catchError(err => of(AnagraficheActions.createClienteFailure({ error: err?.message ?? 'Errore creazione cliente' }))),
          finalize(() => this.loading.hide()),
        );
      })
    )
  );

  updateCliente$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.updateCliente),
      exhaustMap(({ id, changes }) => {
        this.loading.show();
        return this.clientiApi.update(id, changes).pipe(
          map(item => AnagraficheActions.updateClienteSuccess({ item })),
          catchError(err => of(AnagraficheActions.updateClienteFailure({ error: err?.message ?? 'Errore modifica cliente' }))),
          finalize(() => this.loading.hide()),
        );
      })
    )
  );

  deleteCliente$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.deleteCliente),
      exhaustMap(({ id }) => {
        this.loading.show();
        return this.clientiApi.delete(id).pipe(
          map(() => AnagraficheActions.deleteClienteSuccess({ id })),
          catchError(err => of(AnagraficheActions.deleteClienteFailure({ error: err?.message ?? 'Errore eliminazione cliente' }))),
          finalize(() => this.loading.hide()),
        );
      })
    )
  );

  loadClientiPaginated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.loadClientiPaginated),
      exhaustMap(({ params }) =>
        this.clientiApi.getListPaginated(params ?? {}).pipe(
          map(res => AnagraficheActions.loadClientiPaginatedSuccess({
            items: res.content,
            totalElements: res.totalElements,
            totalPages: res.totalPages,
            pageIndex: res.pageIndex,
          })),
          catchError(err => of(AnagraficheActions.loadClientiPaginatedFailure({ error: err?.message ?? 'Errore caricamento clienti' }))),
        )
      )
    )
  );

  refreshPaginatedListsAfterMutations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AnagraficheActions.createAttoreSuccess,
        AnagraficheActions.createFornitoreSuccess,
        AnagraficheActions.createClienteSuccess,
        AnagraficheActions.deleteFornitoreSuccess,
        AnagraficheActions.deleteClienteSuccess
      ),
      withLatestFrom(
        this.store.select(selectFornitoriListLastQuery),
        this.store.select(selectClientiListLastQuery)
      ),
      mergeMap(([action, fornitoriQuery, clientiQuery]) => {
        if (action.type === AnagraficheActions.createAttoreSuccess.type) {
          const actions = [];
          const createdAction = action as ReturnType<typeof AnagraficheActions.createAttoreSuccess>;
          if (createdAction.item.isFornitore) actions.push(AnagraficheActions.loadFornitoriPaginated({ params: fornitoriQuery }));
          if (createdAction.item.isCliente) actions.push(AnagraficheActions.loadClientiPaginated({ params: clientiQuery }));
          return actions;
        }

        if (action.type === AnagraficheActions.createFornitoreSuccess.type) {
          return [AnagraficheActions.loadFornitoriPaginated({ params: fornitoriQuery })];
        }

        if (action.type === AnagraficheActions.createClienteSuccess.type) {
          return [AnagraficheActions.loadClientiPaginated({ params: clientiQuery })];
        }

        if (action.type === AnagraficheActions.deleteFornitoreSuccess.type) {
          return [AnagraficheActions.loadFornitoriPaginated({ params: fornitoriQuery })];
        }

        return [AnagraficheActions.loadClientiPaginated({ params: clientiQuery })];
      })
    )
  );

  refreshFornitoreDetailsAfterAssociate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CantieriActions.associateFornitoreSuccess),
      map(({ idFornitore }) => AnagraficheActions.loadFornitoreDetails({ id: idFornitore }))
    )
  );

  showMutationSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AnagraficheActions.createAttoreSuccess,
          AnagraficheActions.createFornitoreSuccess,
          AnagraficheActions.createClienteSuccess,
          AnagraficheActions.deleteFornitoreSuccess,
          AnagraficheActions.deleteClienteSuccess
        ),
        tap((action) => {
          switch (action.type) {
            case AnagraficheActions.createAttoreSuccess.type:
              this.swalService.success('Anagrafica creata con successo!');
              return;
            case AnagraficheActions.createFornitoreSuccess.type:
              this.swalService.notify('Fornitore creato con successo!', 'success');
              return;
            case AnagraficheActions.createClienteSuccess.type:
              this.swalService.notify('Cliente creato con successo!', 'success');
              return;
            case AnagraficheActions.deleteFornitoreSuccess.type:
              this.swalService.notify('Fornitore eliminato con successo!', 'success');
              return;
            case AnagraficheActions.deleteClienteSuccess.type:
              this.swalService.notify('Cliente eliminato con successo!', 'success');
              return;
          }
        })
      ),
    { dispatch: false }
  );

  showMutationFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AnagraficheActions.createAttoreFailure,
          AnagraficheActions.createFornitoreFailure,
          AnagraficheActions.createClienteFailure,
          AnagraficheActions.deleteFornitoreFailure,
          AnagraficheActions.deleteClienteFailure
        ),
        tap(({ error }) => this.swalService.error(error ?? 'Operazione anagrafica non riuscita'))
      ),
    { dispatch: false }
  );

  updateAttore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.updateAttore),
      switchMap(({ id, dto }) =>
        this.clientiApi.updateAttore(id, dto).pipe(
          map(() => AnagraficheActions.updateAttoreSuccess({ id })),
          catchError(err =>
            of(
              AnagraficheActions.updateAttoreFailure({
                error: err?.message ?? 'Errore aggiornamento anagrafica',
              })
            )
          )
        )
      )
    )
  );
  reloadDetailsAfterUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.updateAttoreSuccess),
      withLatestFrom(this.store.select(selectSelectedFornitore), this.store.select(selectSelectedCliente)),
      map(([{ id }, fornitore, cliente]) => {
        if (fornitore?.id === id) {
          return AnagraficheActions.loadFornitoreDetails({ id });
        }

        return AnagraficheActions.loadClienteDetails({ id });
      })
    )
  );
  showUpdateAttoreSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AnagraficheActions.updateAttoreSuccess),
        tap(() => this.swalService.success('Anagrafica aggiornata con successo'))
      ),
    { dispatch: false }
  );
  showUpdateAttoreFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AnagraficheActions.updateAttoreFailure),
        tap(({ error }) => this.swalService.apiError(error))
      ),
    { dispatch: false }
  );

  createRecapito$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.createRecapito),
      switchMap(({ idAttore, dto }) =>
        this.clientiApi.createRecapito(dto).pipe(
          map(() => AnagraficheActions.createRecapitoSuccess({ idAttore })),
          catchError(err => of(AnagraficheActions.createRecapitoFailure({ error: err?.message ?? 'Errore creazione recapito' })))
        )
      )
    )
  );

  updateRecapito$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.updateRecapito),
      switchMap(({ idAttore, id, dto }) =>
        this.clientiApi.updateRecapito(id, dto).pipe(
          map(() => AnagraficheActions.updateRecapitoSuccess({ idAttore })),
          catchError(err => of(AnagraficheActions.updateRecapitoFailure({ error: err?.message ?? 'Errore aggiornamento recapito' })))
        )
      )
    )
  );

  deleteRecapito$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.deleteRecapito),
      switchMap(({ idAttore, id }) =>
        this.clientiApi.deleteRecapito(id).pipe(
          map(() => AnagraficheActions.deleteRecapitoSuccess({ idAttore })),
          catchError(err => of(AnagraficheActions.deleteRecapitoFailure({ error: err?.message ?? 'Errore eliminazione recapito' })))
        )
      )
    )
  );

  createConto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.createConto),
      switchMap(({ idAttore, dto }) =>
        this.clientiApi.createConto(idAttore, dto).pipe(
          map(() => AnagraficheActions.createContoSuccess({ idAttore })),
          catchError(err => of(AnagraficheActions.createContoFailure({ error: err?.message ?? 'Errore creazione conto' })))
        )
      )
    )
  );

  updateConto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.updateConto),
      switchMap(({ idAttore, id, dto }) =>
        this.clientiApi.updateConto(id, dto).pipe(
          map(() => AnagraficheActions.updateContoSuccess({ idAttore })),
          catchError(err => of(AnagraficheActions.updateContoFailure({ error: err?.message ?? 'Errore aggiornamento conto' })))
        )
      )
    )
  );

  deleteConto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnagraficheActions.deleteConto),
      switchMap(({ idAttore, id }) =>
        this.clientiApi.deleteConto(id).pipe(
          map(() => AnagraficheActions.deleteContoSuccess({ idAttore })),
          catchError(err => of(AnagraficheActions.deleteContoFailure({ error: err?.message ?? 'Errore eliminazione conto' })))
        )
      )
    )
  );
  reloadDetailsAfterRecapitiContiMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AnagraficheActions.createRecapitoSuccess,
        AnagraficheActions.updateRecapitoSuccess,
        AnagraficheActions.deleteRecapitoSuccess,
        AnagraficheActions.createContoSuccess,
        AnagraficheActions.updateContoSuccess,
        AnagraficheActions.deleteContoSuccess,
      ),
      withLatestFrom(
        this.store.select(selectSelectedFornitore),
        this.store.select(selectSelectedCliente),
      ),
      map(([{ idAttore }, fornitore, cliente]) => {
        if (fornitore?.id === idAttore) {
          return AnagraficheActions.loadFornitoreDetails({ id: idAttore });
        }

        return AnagraficheActions.loadClienteDetails({ id: idAttore });
      })
    )
  );
}
